<template>
  <BasePopup
    ref="popup"
    class="upgrade-prompt__popup"
    :fullscreen="true"
    :popout="true"
  >
    <div class="upgrade-prompt">
      <img
        class="upgrade-prompt__img upgrade-prompt__img--light"
        src="@/assets.app/images/upgrade_prompt_hero_light.svg"
      />
      <img
        class="upgrade-prompt__img upgrade-prompt__img--dark"
        src="@/assets.app/images/upgrade_prompt_hero_dark.svg"
      />
      <div class="upgrade-prompt__content">
        <h4 class="upgrade-prompt__content--title">
          Your free trial is waiting.
        </h4>
        <p class="upgrade-prompt__content--text">
          Start your free trial and get unlimited access to every summary on
          Shortform. No charge until your trial ends.
        </p>
      </div>
      <AppButton class="upgrade-prompt__button" @click="gotoBillingPage">
        Start Free Trial
      </AppButton>
    </div>
  </BasePopup>
</template>

<script lang="ts" setup>
import BasePopup from '@/components/ui/BasePopup.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import { onMounted, ref } from 'vue'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { useRouter } from 'vue-router'
import { auth } from '@/services/auth'
import { completionService } from '@/services/completion'
import {
  getUpgradePromptState,
  setUpgradePromptState,
} from '@/models/billing.upgrade.prompt'

const router = useRouter()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)

function canShowDialog(): boolean {
  const isUpgradePromptPending = getUpgradePromptState() === 'pending'
  const isFirstGuidePopupPending = Boolean(
    storage.getItem(StorageKeys.offerGuide),
  )
  const isCompletionPopupOpen19383 = Boolean(completionService.current)
  const userPageVisitCount = Number(
    storage.getItem(StorageKeys.upgradePromptPagesVisited),
  )

  return (
    auth.isFreePlan() &&
    isUpgradePromptPending &&
    !isFirstGuidePopupPending &&
    !isCompletionPopupOpen19383 &&
    userPageVisitCount >= 3 &&
    router.currentRoute.value.name !== 'billing'
  )
}

function gotoBillingPage(): void {
  router.push({ name: 'billing' })
}

onMounted(() => {
  if (canShowDialog()) {
    popup.value?.show()
    setUpgradePromptState('shown')
    storage.removeItem(StorageKeys.upgradePromptPagesVisited)
  }
})
</script>

<style lang="scss" scoped>
.upgrade-prompt {
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media only screen and (max-width: $w-tablet-s) {
    padding-left: 5px;
    padding-right: 5px;
  }

  &__popup {
    --popup-width: 560px;

    @media (max-height: 790px) {
      --popup-width: 462px;

      :deep(.popup__window) {
        padding: 60px 20px 20px;
      }
    }
  }

  &__img {
    width: 272px;
    margin: 0 auto;

    @media (min-width: $w-tablet-s) and (max-height: 790px) {
      width: 200px;
    }

    @media only screen and (max-width: $w-tablet-s) {
      margin-top: 59px;
    }

    &--dark {
      display: none;
    }
  }

  &__content {
    text-align: center;
    padding: 24px 0;

    &--title {
      color: $c-black-2;
      font-size: 28px;
      font-style: normal;
      font-weight: 500;
      line-height: 32px;
      letter-spacing: -0.56px;
      padding-bottom: 12px;
    }

    &--text {
      color: $c-black-2;
      font-size: 16px;
      font-style: normal;
      font-weight: 250;
      line-height: 24px;
      letter-spacing: -0.32px;
    }
  }

  &__button {
    width: 100%;

    :deep(.app-btn__content) {
      padding: 17px 26px 15px 26px;
      height: auto;
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
    }

    @media only screen and (max-width: $w-tablet-s) {
      margin-bottom: 44px;
    }
  }
}

.night-mode {
  .upgrade-prompt {
    &__img {
      &--light {
        display: none;
      }
      &--dark {
        display: block;
      }
    }

    &__content {
      &--title {
        color: $c-black-11;
      }

      &--text {
        color: $c-black-4;
      }
    }
  }
}
</style>
